"use client";

import * as React from "react";
import { Box, Stack, Typography, Dialog, IconButton, Paper } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

type Img = { image_url: string; alt_text?: string | null };

const ASSETS_BASE = "https://www.ritasproperties.com";

/**
 * Fallback: if backend returns relative "/uploads/...", convert to absolute.
 * If backend already returns absolute, keep as-is.
 */
function imgSrc(u?: string | null) {
  if (!u) return "";
  const s = String(u).trim();
  if (!s) return "";
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/")) return `${ASSETS_BASE}${s}`;
  return `${ASSETS_BASE}/${s}`;
}

/**
 * Build gallery list:
 * - start with featured (if any)
 * - add images (dedupe by canonical src)
 */
function safeImgs(featured?: string | null, images?: Img[]) {
  const out: Img[] = [];
  const seen = new Set<string>();

  const add = (im?: Img | null) => {
    const url = im?.image_url ? imgSrc(im.image_url) : "";
    if (!url) return;
    if (seen.has(url)) return;
    seen.add(url);
    out.push({ image_url: url, alt_text: im?.alt_text ?? "" });
  };

  if (featured) add({ image_url: featured, alt_text: "" });
  (images || []).forEach((i) => add(i));

  return out;
}

export default function PropertyGallery({
  title,
  featured,
  images,
}: {
  title: string;
  featured?: string | null;
  images?: Img[];
}) {
  const items = React.useMemo(() => safeImgs(featured, images), [featured, images]);
  const [open, setOpen] = React.useState(false);
  const [idx, setIdx] = React.useState(0);

  const openAt = (i: number) => {
    setIdx(i);
    setOpen(true);
  };

  const prev = React.useCallback(() => {
    setIdx((p) => (p - 1 + items.length) % items.length);
  }, [items.length]);

  const next = React.useCallback(() => {
    setIdx((p) => (p + 1) % items.length);
  }, [items.length]);

  // ✅ Swipe support (mobile)
  const touchStartX = React.useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const startX = touchStartX.current;
    if (startX == null || items.length <= 1) return;

    const endX = e.changedTouches[0]?.clientX ?? startX;
    const dx = endX - startX;

    if (Math.abs(dx) > 40) {
      if (dx > 0) prev();
      else next();
    }
    touchStartX.current = null;
  };

  // ✅ Keyboard support (desktop)
  React.useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, prev, next]);

  if (!items.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          p: 2,
        }}
      >
        <Typography color="text.secondary">No photos</Typography>
      </Paper>
    );
  }

  const cover = items[0];

  const iconGlassSx = {
    color: "white",
    bgcolor: "rgba(0,0,0,0.35)",
    backdropFilter: "blur(6px)",
    border: "1px solid rgba(255,255,255,0.18)",
    "&:hover": { bgcolor: "rgba(0,0,0,0.5)" },
  };

  const navBtnSx = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 10,
    width: 44,
    height: 44,
    ...iconGlassSx,
  };

  return (
    <Stack spacing={1.5}>
      {/* Cover */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: { xs: "16/10", md: "21/9" },
            bgcolor: "grey.100",
            cursor: "zoom-in",
          }}
          onClick={() => openAt(0)}
        >
          <img
            src={cover.image_url}
            alt={cover.alt_text ?? title}
            draggable={false}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              userSelect: "none",
              WebkitUserSelect: "none",
            }}
          />

          {/* subtle gradient for premium look */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(to top, rgba(0,0,0,0.22), rgba(0,0,0,0.0) 55%)",
              pointerEvents: "none",
            }}
          />
        </Box>
      </Paper>

      {/* Thumbnails (horizontal scroll) */}
      {items.length > 1 ? (
        <Box
          sx={{
            display: "flex",
            gap: 1.2,
            overflowX: "auto",
            pb: 0.5,
            "::-webkit-scrollbar": { height: 8 },
            "::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(150,150,150,0.35)",
              borderRadius: 999,
            },
          }}
        >
          {items.slice(0, 12).map((im, i) => (
            <Paper
              key={im.image_url + i}
              elevation={0}
              onClick={() => openAt(i)}
              sx={{
                flex: "0 0 auto",
                width: { xs: 104, sm: 120 },
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                cursor: "pointer",
                opacity: i === idx ? 1 : 0.9,
                transform: i === idx ? "scale(1.02)" : "none",
                transition: "transform 120ms ease",
              }}
            >
              <Box sx={{ aspectRatio: "4/3", bgcolor: "grey.100" }}>
                <img
                  src={im.image_url}
                  alt={im.alt_text ?? title}
                  draggable={false}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                  }}
                />
              </Box>
            </Paper>
          ))}
        </Box>
      ) : null}

      {/* Lightbox */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <Box
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          sx={{
            position: "relative",
            bgcolor: "black",
            borderRadius: 2,
            overflow: "hidden",
            height: { xs: "78vh", md: "80vh" },
            touchAction: "pan-y", // allow vertical scroll; we handle horizontal swipe
          }}
        >
          {/* Close */}
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ position: "absolute", top: 12, right: 12, zIndex: 10, ...iconGlassSx }}
          >
            <CloseIcon />
          </IconButton>

          <Box
            sx={{
              position: "absolute",
              right: 12,
              top: 56,
              color: "rgba(255,255,255,0.75)",
              fontSize: 12,
              zIndex: 10,
            }}
          >
            Κλείσιμο: X ή κλικ έξω • Swipe ↔
          </Box>

          {/* Nav */}
          {items.length > 1 ? (
            <>
              <IconButton onClick={prev} sx={{ ...navBtnSx, left: 12 }} aria-label="Previous photo">
                <ChevronLeftIcon />
              </IconButton>

              <IconButton onClick={next} sx={{ ...navBtnSx, right: 12 }} aria-label="Next photo">
                <ChevronRightIcon />
              </IconButton>
            </>
          ) : null}

          {/* Image */}
          <Box sx={{ width: "100%", height: "100%", display: "grid", placeItems: "center" }}>
            <img
              src={items[idx].image_url}
              alt={items[idx].alt_text ?? title}
              draggable={false}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                display: "block",
                userSelect: "none",
                WebkitUserSelect: "none",
              }}
            />
          </Box>
        </Box>
      </Dialog>
    </Stack>
  );
}
